/**
 * Content Parser Component
 * 
 * Parses workout content and renders it with Help Mode highlighting.
 * Handles both structured JSON and plain text content formats.
 */

import React, { useCallback, useMemo } from 'react';
import { useHelpMode } from './HelpModeProvider';
import { debugLog } from '../../utils/debug';

export const ContentParser = ({ 
  content, 
  workoutContext, 
  className = "",
  ...props 
}) => {
  const { isHelpMode, handleContentClick } = useHelpMode();

  const parsePlainText = useCallback((details) => {
    if (!details) return [];

    const sections = [];

    // First, try to split by common workout structure patterns (including Markdown bold)
    const workoutPatterns = [
      /(?:Workout Overview|Overview):?/i,
      /(?:Dynamic Warm-Up|Warm-Up|Warmup):?/i,
      /(?:Main Run|Main Set|Main Workout):?/i,
      /(?:Cool-Down|Cool Down|Cooldown):?/i,
      /(?:Pacing|Pace|Effort|Intensity):?/i,
      /(?:Instructions|Execution|Technique):?/i,
      /(?:Key Focus|Focus):?/i,
      /(?:Workout Structure):?/i,
      /(?:Technical Focus):?/i,
      /(?:Execution Tips):?/i
    ];

    // Also check for Markdown bold patterns
    const markdownPatterns = [
      /\*\*(?:Workout Overview|Overview):\*\*/i,
      /\*\*(?:Dynamic Warm-Up|Warm-Up|Warmup):\*\*/i,
      /\*\*(?:Main Run|Main Set|Main Workout):\*\*/i,
      /\*\*(?:Cool-Down|Cool Down|Cooldown):\*\*/i,
      /\*\*(?:Pacing|Pace|Effort|Intensity):\*\*/i,
      /\*\*(?:Instructions|Execution|Technique):\*\*/i,
      /\*\*(?:Key Focus|Focus):\*\*/i
    ];

    // Try to split the content by these patterns
    let hasStructure = false;
    for (const pattern of [...workoutPatterns, ...markdownPatterns]) {
      if (pattern.test(details)) {
        hasStructure = true;
        break;
      }
    }

    if (hasStructure) {
      // Split by double newlines first, then by patterns
      const paragraphs = details.split(/\n\s*\n/).filter(p => p.trim());

      for (const paragraph of paragraphs) {
        const trimmedPara = paragraph.trim();

        // Check if this paragraph starts with a header pattern (including Markdown bold)
        // Handle both "Header:" and "Header" formats
        const headerMatch = trimmedPara.match(/^(\*\*)?([^:]+):?\*\*\s*(.*)$/s);
        if (headerMatch) {
          const [, markdownBold, headerText, content] = headerMatch;
          sections.push({
            type: 'header',
            content: headerText.trim(),
            level: 1
          });

          // Parse the content as list items if it contains bullet points
          const listItems = content.split(/\n/).filter(line =>
            line.trim().match(/^[-*•]\s+/) || line.trim().match(/^\d+\.\s+/)
          );

          if (listItems.length > 0) {
            sections.push({
              type: 'list',
              items: listItems.map(item => ({
                content: item.replace(/^[-*•]\s+/, '').replace(/^\d+\.\s+/, ''),
                highlightable: true
              }))
            });
          } else if (content.trim()) {
            sections.push({
              type: 'paragraph',
              content: content.trim(),
              highlightable: true
            });
          }
        } else {
          // Regular paragraph
          sections.push({
            type: 'paragraph',
            content: trimmedPara,
            highlightable: true
          });
        }
      }
    } else {
      // Fallback to original line-by-line parsing
      const lines = details.split('\n').filter(line => line.trim());
      let currentSection = null;

      for (const line of lines) {
        const trimmedLine = line.trim();

        if (trimmedLine.startsWith('#') || trimmedLine.match(/^[A-Z][^:]*:?$/)) {
          if (currentSection) sections.push(currentSection);
          currentSection = {
            type: 'header',
            content: trimmedLine.replace(/^#+\s*/, ''),
            level: trimmedLine.match(/^#+/) ? trimmedLine.match(/^#+/)[0].length : 1
          };
        } else if (trimmedLine.startsWith('* ') || trimmedLine.startsWith('- ')) {
          if (!currentSection || currentSection.type !== 'list') {
            if (currentSection) sections.push(currentSection);
            currentSection = { type: 'list', items: [] };
          }
          currentSection.items.push({
            content: trimmedLine.replace(/^[*\-]\s*/, ''),
            highlightable: true
          });
        } else if (trimmedLine) {
          if (!currentSection || currentSection.type !== 'paragraph') {
            if (currentSection) sections.push(currentSection);
            currentSection = { type: 'paragraph', content: trimmedLine };
          } else {
            currentSection.content += ' ' + trimmedLine;
          }
        }
      }

      if (currentSection) sections.push(currentSection);
    }

    return sections;
  }, []);

  const parseMarkdownContent = useCallback((content) => {
    if (!content) return [];

    const sections = [];
    
    // First, try to handle inline bold format within a single paragraph
    // This handles cases like "**Distance:** 8km... **Focus:** ... **Purpose:** ..."
    if (content.includes('**') && !content.includes('\n\n')) {
      // For mobile-first optimization, combine headers and content into compact format
      const boldPattern = /\*\*([^:]+):\*\*\s*([^*]+?)(?=\*\*|$)/g;
      let match;
      const compactItems = [];
      
      while ((match = boldPattern.exec(content)) !== null) {
        const [, headerText, contentText] = match;
        compactItems.push(`${headerText.trim()}: ${contentText.trim()}`);
      }
      
      // If we found structured content, return as a single compact paragraph
      if (compactItems.length > 0) {
        sections.push({
          type: 'paragraph',
          content: compactItems.join('\n'),
          highlightable: true
        });
        return sections;
      }
    }
    
    // Fallback to paragraph-based parsing
    // Split content by double newlines to get paragraphs
    const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim());
    
    for (const paragraph of paragraphs) {
      const trimmedPara = paragraph.trim();
      
      // Check if this paragraph starts with a Markdown bold header (like **Warm-up:**)
      const headerMatch = trimmedPara.match(/^\*\*([^:]+):\*\*\s*(.*)$/s);
      
      if (headerMatch) {
        const [, headerText, content] = headerMatch;
        
        // Add the header
        sections.push({
          type: 'header',
          content: headerText.trim(),
          level: 1
        });
        
        // Add the content as a paragraph
        if (content.trim()) {
          sections.push({
            type: 'paragraph',
            content: content.trim(),
            highlightable: true
          });
        }
      } else {
        // Check if this paragraph starts with a plain text header (like "Workout Overview")
        // Look for common header patterns that are standalone lines
        const plainHeaderPatterns = [
          /^(Workout Overview|Overview)$/i,
          /^(Workout Structure|Structure)$/i,
          /^(Technical Focus|Focus)$/i,
          /^(Execution Tips|Tips)$/i,
          /^(Key Focus|Key Points)$/i,
          /^(Warm-Up|Warmup)$/i,
          /^(Main Set|Main Workout)$/i,
          /^(Cool-Down|Cooldown)$/i,
          /^(Pacing|Pace)$/i,
          /^(Instructions|Execution)$/i,
          /^(Recovery Markers|Recovery)$/i
        ];
        
        // Check if this paragraph contains a header followed by content
        // Pattern: "Header\nContent" or "Header\n\nContent"
        const headerWithContentMatch = trimmedPara.match(/^([^\n]+)\n\s*(.*)$/s);
        
        if (headerWithContentMatch) {
          const [, potentialHeader, content] = headerWithContentMatch;
          const isPlainHeader = plainHeaderPatterns.some(pattern => pattern.test(potentialHeader.trim()));
          
          if (isPlainHeader) {
            // This is a plain text header with content - add it as a header
            sections.push({
              type: 'header',
              content: potentialHeader.trim(),
              level: 1
            });
            
            // Add the content as a paragraph
            if (content.trim()) {
              sections.push({
                type: 'paragraph',
                content: content.trim(),
                highlightable: true
              });
            }
          } else {
            // Regular paragraph
            sections.push({
              type: 'paragraph',
              content: trimmedPara,
              highlightable: true
            });
          }
        } else {
          // Check if this is just a standalone header
          const isPlainHeader = plainHeaderPatterns.some(pattern => pattern.test(trimmedPara));
          
          if (isPlainHeader) {
            // This is a standalone plain text header - add it as a header
            sections.push({
              type: 'header',
              content: trimmedPara.trim(),
              level: 1
            });
          } else {
            // Regular paragraph
            sections.push({
              type: 'paragraph',
              content: trimmedPara,
              highlightable: true
            });
          }
        }
      }
    }
    
    return sections;
  }, []);

  const parseContent = useCallback((content) => {
    if (!content) return [];

    let sections = [];

    try {
      // Try to parse as structured JSON data first
      let jsonString = content.trim();
      

      // Remove common prefixes and suffixes
      if (jsonString.startsWith('json ')) {
        jsonString = jsonString.substring(5);
      }
      if (jsonString.startsWith('```json')) {
        jsonString = jsonString.substring(7);
      }
      if (jsonString.endsWith('```')) {
        jsonString = jsonString.substring(0, jsonString.length - 3);
      }

      const structuredData = JSON.parse(jsonString.trim());

      if (structuredData.sections) {
        sections = structuredData.sections;
      } else if (structuredData.type === 'multi_session' && structuredData.sessions) {
        // Handle multi-session workout format
        debugLog('ContentParser: Processing multi-session with', structuredData.sessions.length, 'sessions');
        sections = structuredData.sessions.flatMap((session, index) => {
          // Parse each session's plan - check if it's enhanced content with headers
          debugLog('ContentParser: Parsing session', session.name, 'with plan:', session.plan.substring(0, 100) + '...');
          
          // Clean the session plan - remove session name if it appears at the beginning
          let cleanedPlan = session.plan;
          if (cleanedPlan.startsWith(session.name)) {
            cleanedPlan = cleanedPlan.substring(session.name.length).trim();
            // Remove any leading newlines
            cleanedPlan = cleanedPlan.replace(/^\n+/, '');
            debugLog('ContentParser: Removed session name from plan content');
          } else if (cleanedPlan.includes(session.name + '\n\n')) {
            // Also check for session name followed by double newline
            cleanedPlan = cleanedPlan.replace(session.name + '\n\n', '');
            debugLog('ContentParser: Removed session name with double newline from plan content');
          } else if (cleanedPlan.includes(session.name + '\n')) {
            // Check for session name followed by single newline
            cleanedPlan = cleanedPlan.replace(session.name + '\n', '');
            debugLog('ContentParser: Removed session name with single newline from plan content');
          }
          
          // Check if this session has enhanced content with structured headers
          const hasStructuredHeaders = cleanedPlan.includes('Workout Overview') || 
                                     cleanedPlan.includes('Workout Structure') || 
                                     cleanedPlan.includes('Technical Focus') || 
                                     cleanedPlan.includes('Execution Tips');
          
          // Also check for inline bold format (like **Distance:**, **Focus:**, **Purpose:**)
          const hasInlineBoldFormat = cleanedPlan.includes('**Distance:**') || 
                                    cleanedPlan.includes('**Focus:**') || 
                                    cleanedPlan.includes('**Purpose:**') || 
                                    cleanedPlan.includes('**Warm-up:**') || 
                                    cleanedPlan.includes('**Main Set:**') || 
                                    cleanedPlan.includes('**Cool-down:**') ||
                                    cleanedPlan.includes('**Key Focus:**');
          
          debugLog('ContentParser: Session', session.name, 'cleaned plan preview:', cleanedPlan.substring(0, 200) + '...');
          debugLog('ContentParser: Session', session.name, 'has structured headers:', hasStructuredHeaders);
          debugLog('ContentParser: Session', session.name, 'has inline bold format:', hasInlineBoldFormat);
          
          let sessionSections;
          if (hasStructuredHeaders) {
            debugLog('ContentParser: Session', session.name, 'has enhanced content, using markdown parser');
            sessionSections = parseMarkdownContent(cleanedPlan);
          } else if (hasInlineBoldFormat) {
            debugLog('ContentParser: Session', session.name, 'has inline bold format, using markdown parser');
            sessionSections = parseMarkdownContent(cleanedPlan);
          } else {
            debugLog('ContentParser: Session', session.name, 'has basic content, using plain text parser');
            sessionSections = parsePlainText(cleanedPlan);
          }
          debugLog('ContentParser: Session', session.name, 'parsed into', sessionSections.length, 'sections');
          
          // Create header + content pairs for each session
          const sessionHeader = {
            type: 'header',
            content: session.name,
            level: 2,
            highlightable: false
          };
          
          const sessionContent = sessionSections.map(section => ({
            ...section,
            content: section.content
          }));
          
          // Return header first, then content for this session
          return [sessionHeader, ...sessionContent];
        });
        debugLog('ContentParser: Total sections generated:', sections.length);
      }
    } catch (e) {
      // Check if content contains Markdown bold patterns (like **Warm-up:**)
      if (content.includes('**') && content.includes(':')) {
        // This looks like Markdown format, parse it specially
        sections = parseMarkdownContent(content);
      } else {
        // Fallback to plain text parsing for existing data
        sections = parsePlainText(content);
      }
    }
    
    return sections;
  }, [parsePlainText, parseMarkdownContent]);

  const parsedSections = useMemo(() => {
    return parseContent(content);
  }, [content, parseContent]);

  const renderStructuredContent = useCallback((sections) => {
    return sections.map((section, index) => {
      if (section.type === 'header') {
        const headerClass = section.level === 1
          ? 'text-base font-bold text-white mt-2 mb-1 border-b border-blue-400 pb-1'
          : section.level === 2
          ? 'text-base font-bold text-white mt-2 mb-1 border-b border-blue-500 pb-1'
          : 'text-base font-semibold text-white mt-2 mb-1 border-b border-gray-600 pb-1';

        return (
          <h3 key={index} className={headerClass}>
            <span dangerouslySetInnerHTML={{
              __html: section.content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-blue-300">$1</strong>')
            }} />
          </h3>
        );
      }

      if (section.type === 'list') {
        return (
          <ul key={index} className="list-disc list-inside space-y-1 my-2">
            {section.items.map((item, itemIndex) => {
              // Make all list items highlightable in help mode for consistent UX
              const isHighlightable = isHelpMode;

              if (isHighlightable) {
                return (
                  <li
                    key={itemIndex}
                    className="text-gray-200 help-mode-highlight bg-yellow-400/30 border-2 border-yellow-400/80 rounded-md px-3 py-2 cursor-pointer hover:bg-yellow-400/40 transition-all duration-200 mb-2"
                    data-instruction={item.content}
                    onClick={(e) => handleContentClick(item.content, workoutContext)}
                  >
                    <span dangerouslySetInnerHTML={{
                      __html: item.content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-blue-300">$1</strong>')
                    }} />
                  </li>
                );
              } else {
                return (
                  <li key={itemIndex} className="text-gray-200 mb-2">
                    <span dangerouslySetInnerHTML={{
                      __html: item.content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-blue-300">$1</strong>')
                    }} />
                  </li>
                );
              }
            })}
          </ul>
        );
      }

      if (section.type === 'paragraph') {
        // Make all paragraphs highlightable in help mode for consistent UX
        const isHighlightable = isHelpMode;

        if (isHighlightable) {
          return (
            <p
              key={index}
              className="text-gray-200 my-1 help-mode-highlight bg-yellow-400/30 border-2 border-yellow-400/80 rounded-md px-3 py-2 cursor-pointer hover:bg-yellow-400/40 transition-all duration-200"
              data-instruction={section.content}
              onClick={(e) => handleContentClick(section.content, workoutContext)}
            >
              <span dangerouslySetInnerHTML={{
                __html: section.content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-blue-300">$1</strong>')
              }} />
            </p>
          );
        } else {
          return (
            <p key={index} className="text-gray-200 my-1">
              <span dangerouslySetInnerHTML={{
                __html: section.content.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-blue-300">$1</strong>')
              }} />
            </p>
          );
        }
      }

      return null;
    });
  }, [isHelpMode, handleContentClick, workoutContext]);

  if (!content) return null;

  return (
    <div
      className={`workout-details-content text-gray-200 bg-gradient-to-br from-blue-500/12 to-blue-600/12 border border-blue-400/30 rounded-lg p-4 backdrop-blur-sm shadow-lg space-y-2 ${className}`}
      {...props}
    >
      {renderStructuredContent(parsedSections)}
    </div>
  );
};
